import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, TaxDocument, maskTaxId, formatTaxId, validateTaxId } from '../../lib/supabase';
import { DocumentUpload } from './DocumentUpload';
import {
  FileText,
  Shield,
  CheckCircle2,
  AlertTriangle,
  Eye,
  EyeOff,
  Upload,
  Building2,
  Calendar,
  Save,
  XCircle
} from 'lucide-react';

export const TaxComplianceManager: React.FC = () => {
  const { profile } = useAuth();
  const [taxDoc, setTaxDoc] = useState<TaxDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTaxId, setShowTaxId] = useState(false);
  const [uploadingW9, setUploadingW9] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    tax_id: '',
    tax_id_type: 'ein' as 'ein' | 'ssn',
    business_legal_name: '',
    tax_classification: 'sole_proprietor' as TaxDocument['tax_classification']
  });

  useEffect(() => {
    loadTaxDocument();
  }, [profile]);

  const loadTaxDocument = async () => {
    if (!profile) return;

    try {
      const { data, error } = await supabase
        .from('tax_documents')
        .select('*')
        .eq('user_id', profile.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setTaxDoc(data);
        setFormData({
          tax_id: data.tax_id || '',
          tax_id_type: data.tax_id_type || 'ein',
          business_legal_name: data.business_legal_name || '',
          tax_classification: data.tax_classification || 'sole_proprietor'
        });
      }
    } catch (error) {
      console.error('Error loading tax document:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile) return;

    if (formData.tax_id && !validateTaxId(formData.tax_id, formData.tax_id_type)) {
      alert(`Invalid ${formData.tax_id_type.toUpperCase()} format. Please enter 9 digits.`);
      return;
    }

    setSaving(true);
    try {
      const formattedTaxId = formData.tax_id
        ? formatTaxId(formData.tax_id, formData.tax_id_type)
        : null;

      const docData = {
        user_id: profile.id,
        tax_id: formattedTaxId,
        tax_id_type: formData.tax_id_type,
        business_legal_name: formData.business_legal_name || null,
        tax_classification: formData.tax_classification,
        updated_at: new Date().toISOString()
      };

      if (taxDoc) {
        const { error } = await supabase
          .from('tax_documents')
          .update(docData)
          .eq('id', taxDoc.id);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('tax_documents')
          .insert(docData)
          .select()
          .single();

        if (error) throw error;
        if (data) setTaxDoc(data);
      }

      await loadTaxDocument();
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving tax document:', error);
      alert('Failed to save tax information');
    } finally {
      setSaving(false);
    }
  };

  const handleW9Upload = async (url: string) => {
    if (!profile) return;

    try {
      const docData = {
        user_id: profile.id,
        w9_document_url: url,
        w9_submission_date: new Date().toISOString().split('T')[0],
        updated_at: new Date().toISOString()
      };

      if (taxDoc) {
        const { error } = await supabase
          .from('tax_documents')
          .update(docData)
          .eq('id', taxDoc.id);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('tax_documents')
          .insert(docData)
          .select()
          .single();

        if (error) throw error;
        if (data) setTaxDoc(data);
      }

      await loadTaxDocument();
      setUploadingW9(false);
    } catch (error) {
      console.error('Error uploading W-9:', error);
      alert('Failed to upload W-9 document');
    }
  };

  const getComplianceStatus = () => {
    const hasW9 = taxDoc?.w9_document_url;
    const hasTaxId = taxDoc?.tax_id;
    const isVerified = taxDoc?.w9_verified;

    if (isVerified && hasW9 && hasTaxId) {
      return { status: 'complete', message: 'Fully Compliant', color: 'green' };
    } else if (hasW9 && hasTaxId) {
      return { status: 'pending', message: 'Pending Verification', color: 'amber' };
    } else if (hasW9 || hasTaxId) {
      return { status: 'partial', message: 'Incomplete', color: 'orange' };
    }
    return { status: 'none', message: 'Not Started', color: 'slate' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const complianceStatus = getComplianceStatus();

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-blue-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Tax Compliance Status</h3>
            <p className="text-sm text-slate-600 mt-1">Manage your tax documents and identification</p>
          </div>
          <div className={`px-4 py-2 rounded-full border-2 ${
            complianceStatus.status === 'complete'
              ? 'bg-green-50 border-green-200 text-green-700'
              : complianceStatus.status === 'pending'
              ? 'bg-amber-50 border-amber-200 text-amber-700'
              : complianceStatus.status === 'partial'
              ? 'bg-orange-50 border-orange-200 text-orange-700'
              : 'bg-slate-100 border-slate-300 text-slate-700'
          }`}>
            <div className="flex items-center space-x-2">
              {complianceStatus.status === 'complete' ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                <AlertTriangle className="w-5 h-5" />
              )}
              <span className="font-semibold">{complianceStatus.message}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 ${
            taxDoc?.tax_id
              ? 'bg-green-50 border-green-200'
              : 'bg-slate-50 border-slate-200'
          }`}>
            <Shield className={`w-8 h-8 ${taxDoc?.tax_id ? 'text-green-600' : 'text-slate-400'}`} />
            <div className="flex-1">
              <div className="font-medium text-slate-900">Tax ID</div>
              <div className="text-sm text-slate-600">
                {taxDoc?.tax_id ? 'Provided' : 'Not provided'}
              </div>
            </div>
            {taxDoc?.tax_id && <CheckCircle2 className="w-5 h-5 text-green-600" />}
          </div>

          <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 ${
            taxDoc?.w9_document_url
              ? taxDoc.w9_verified
                ? 'bg-green-50 border-green-200'
                : 'bg-amber-50 border-amber-200'
              : 'bg-slate-50 border-slate-200'
          }`}>
            <FileText className={`w-8 h-8 ${
              taxDoc?.w9_document_url
                ? taxDoc.w9_verified
                  ? 'text-green-600'
                  : 'text-amber-600'
                : 'text-slate-400'
            }`} />
            <div className="flex-1">
              <div className="font-medium text-slate-900">W-9 Form</div>
              <div className="text-sm text-slate-600">
                {taxDoc?.w9_document_url
                  ? taxDoc.w9_verified
                    ? 'Verified'
                    : 'Pending verification'
                  : 'Not uploaded'}
              </div>
            </div>
            {taxDoc?.w9_verified && <CheckCircle2 className="w-5 h-5 text-green-600" />}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-900 flex items-center">
            <Shield className="w-5 h-5 mr-2 text-blue-600" />
            Tax Identification
          </h3>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              {taxDoc?.tax_id ? 'Edit' : 'Add Tax ID'}
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Tax ID Type
              </label>
              <select
                value={formData.tax_id_type}
                onChange={(e) => setFormData({ ...formData, tax_id_type: e.target.value as 'ein' | 'ssn' })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                <option value="ein">EIN (Employer Identification Number)</option>
                <option value="ssn">SSN (Social Security Number)</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">
                {formData.tax_id_type === 'ein'
                  ? 'Format: XX-XXXXXXX (9 digits)'
                  : 'Format: XXX-XX-XXXX (9 digits)'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Tax ID Number
              </label>
              <input
                type="text"
                value={formData.tax_id}
                onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                placeholder={formData.tax_id_type === 'ein' ? '12-3456789' : '123-45-6789'}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                maxLength={11}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Business Legal Name
              </label>
              <input
                type="text"
                value={formData.business_legal_name}
                onChange={(e) => setFormData({ ...formData, business_legal_name: e.target.value })}
                placeholder="As shown on tax documents"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Tax Classification
              </label>
              <select
                value={formData.tax_classification}
                onChange={(e) => setFormData({ ...formData, tax_classification: e.target.value as TaxDocument['tax_classification'] })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                <option value="individual">Individual</option>
                <option value="sole_proprietor">Sole Proprietor</option>
                <option value="c_corp">C Corporation</option>
                <option value="s_corp">S Corporation</option>
                <option value="partnership">Partnership</option>
                <option value="llc">LLC</option>
              </select>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:bg-slate-300 flex items-center justify-center"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Tax ID
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setIsEditing(false);
                  if (taxDoc) {
                    setFormData({
                      tax_id: taxDoc.tax_id || '',
                      tax_id_type: taxDoc.tax_id_type || 'ein',
                      business_legal_name: taxDoc.business_legal_name || '',
                      tax_classification: taxDoc.tax_classification || 'sole_proprietor'
                    });
                  }
                }}
                className="px-4 py-2.5 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : taxDoc?.tax_id ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Building2 className="w-5 h-5 text-slate-600" />
                <div>
                  <div className="text-sm text-slate-600">Tax ID</div>
                  <div className="font-mono font-medium text-slate-900">
                    {showTaxId ? taxDoc.tax_id : maskTaxId(taxDoc.tax_id)}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowTaxId(!showTaxId)}
                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                title={showTaxId ? 'Hide' : 'Show'}
              >
                {showTaxId ? (
                  <EyeOff className="w-5 h-5 text-slate-600" />
                ) : (
                  <Eye className="w-5 h-5 text-slate-600" />
                )}
              </button>
            </div>

            {taxDoc.business_legal_name && (
              <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                <Building2 className="w-5 h-5 text-slate-600" />
                <div>
                  <div className="text-sm text-slate-600">Business Name</div>
                  <div className="font-medium text-slate-900">{taxDoc.business_legal_name}</div>
                </div>
              </div>
            )}

            {taxDoc.tax_classification && (
              <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                <FileText className="w-5 h-5 text-slate-600" />
                <div>
                  <div className="text-sm text-slate-600">Tax Classification</div>
                  <div className="font-medium text-slate-900 capitalize">
                    {taxDoc.tax_classification.replace('_', ' ')}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 mb-4">No tax identification on file</p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-900 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-blue-600" />
            W-9 Form
          </h3>
          {taxDoc?.w9_verified && (
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full border border-green-200">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm font-medium">Verified</span>
            </div>
          )}
        </div>

        {taxDoc?.w9_document_url ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <div>
                  <div className="font-medium text-green-900">W-9 Uploaded</div>
                  {taxDoc.w9_submission_date && (
                    <div className="text-sm text-green-700 flex items-center mt-1">
                      <Calendar className="w-3 h-3 mr-1" />
                      Submitted {new Date(taxDoc.w9_submission_date).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <a
                  href={taxDoc.w9_document_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  View
                </a>
                <button
                  onClick={() => setUploadingW9(true)}
                  className="px-3 py-1.5 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Replace
                </button>
              </div>
            </div>

            {uploadingW9 && profile && (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <DocumentUpload
                  userId={profile.id}
                  documentType="w9"
                  onUploadComplete={handleW9Upload}
                  onCancel={() => setUploadingW9(false)}
                  acceptedFileTypes=".pdf"
                />
              </div>
            )}
          </div>
        ) : (
          <div>
            {profile && (
              <DocumentUpload
                userId={profile.id}
                documentType="w9"
                onUploadComplete={handleW9Upload}
                acceptedFileTypes=".pdf"
              />
            )}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Important:</strong> Upload a completed IRS Form W-9. This is required for 1099 tax reporting.
                Only PDF files are accepted.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
